<template>
  <div class="product-family-head-variant-fi">
    <div class="product-family-head-variant-fi__data">
      <link-fi
        class="product-family-head-variant-fi__data__title"
        primary
        :href="variantSlug"
        :title="catalogData.ProductName"
        :size="18"
        @link-click="onProductLinkClick"
      >
        {{ catalogData.ProductName }}
      </link-fi>

      <div
        v-if="catalogData.ProductId"
        class="product-family-head-variant-fi__data__article-number"
      >
        {{ $dict.get('product-List.Table.Article-Number') }}
        {{ catalogData.ProductId }}
      </div>
    </div>

    <template v-if="isShop">
      <div
        v-if="!bestPrice && loadingBestPrice"
        class="product-family-head-variant-fi__best-price__loading"
      >
        <loading-indicator
          variant="spinnerLocal"
          transparent
        />
      </div>

      <template v-else-if="bestPrice">
        <div class="product-family-head-variant-fi__title">
          {{ $dict.get('product-Family.Facets.Variant.Title') }}
        </div>

        <div class="product-family-head-variant-fi__best-price">
          <div
            v-if="isNotOrderable"
            class="product-family-head-variant-fi__not-orderable"
            data-test="discontinued-not-orderable"
          >
            {{ $dict.get('online-Shop.Product.Discontinued-Not-Orderable') }}
          </div>

          <div
            v-else
            class="product-family-head-variant-fi__quantity"
          >
            <quantity-steps
              ref="quantitySteps"
              class="theme--blocked"
              template="article"
              :requested-quantity="quantity"
              :item="bestPrice"
              :max-quantity="cappedQuantity"
              :max-quantity-raw="remainingStock"
              large
              light
              @update-quantity="updateQuantity"
              @user-input="updateQuantity"
              @value-rounded="onValueRounded"
            />

            <product-package-size-fi
              class="product-family-head-variant-fi__package-size"
              :item="bestPrice"
              align-right
            />
          </div>

          <quantity-modification-hint-fi
            v-if="quantityModificationHint"
            class="product-family-head-variant-fi__quantity-hint"
            :hint="quantityModificationHint"
            :data-test-2="quantityModificationDataTest2"
            icon="info"
          />

          <div class="product-family-head-variant-fi__price">
            <loading-indicator-wrapper
              class="product-family-head-variant-fi__price__loading-indicator"
              :loading="loadingBestPrice"
            >
              <price-savings-fi
                class="product-family-head-variant-fi__price__price"
                :best-price="bestPrice"
                :use-eco="fischer.shop.useEco()"
                hide-savings-in-percent
              />

              <div class="product-family-head-variant-fi__price__info">
                {{ $dict.get('online-Shop.Product.Vat-excluded') }},
                {{ $dict.get('online-Shop.Product.Plus-Shipping') }}
              </div>

              <base-price-fi
                :best-price="bestPrice"
                show-label
              />

              <availability-status
                class="product-family-head-variant-fi__price__availability-status"
                :delivery-speed="bestPrice.deliverySpeed || 'NODELIVERY'"
                :stock-levels="bestPrice.stockLevels || null"
                :is-seefelder
                align-right
                disable-tooltip
                :article-number="variant.ID"
                :requested-quantity="quantity"
              />
            </loading-indicator-wrapper>
          </div>
        </div>
      </template>
    </template>

    <div class="product-family-head-variant-fi__actions">
      <template v-if="isShop">
        <template v-if="bestPrice">
          <button-fi
            v-if="useMemoList && !isAppView && isAvailable && !isNotOrderable"
            type="button"
            class="product-family-head-variant-fi__action__add-to-memolist button-fi__tertiary button-fi--large"
            data-test="add-to-wish-list"
            :disabled="Boolean(loadingBestPrice || addToCartLoading)"
            @click="addToMemoList"
          >
            {{ $dict.get('online-Shop.Product.Add-to-Memo-List') }}
          </button-fi>
          <button-fi
            v-if="!isNotOrderable"
            type="button"
            class="product-family-head-variant-fi__action__add-to-cart button-fi__primary button-fi--large"
            data-test="add-to-cart"
            :data-test-2="
              Boolean(loadingBestPrice || addToCartLoading) ? 'add-to-cart-disabled' : null
            "
            :disabled="Boolean(loadingBestPrice || addToCartLoading)"
            @click.prevent="addProduct"
          >
            {{ $dict.get('online-Shop.Product.Add-to-Cart') }}
          </button-fi>
        </template>

        <div
          v-else-if="!loadingBestPrice"
          class="product-family-head-variant-fi__expired"
        >
          {{ $dict.get('online-Shop.Product.Expired') }}
        </div>
      </template>

      <template
        v-else-if="
          (isRetailConnect || isRetailConnectCart || localRetailers) && !isSystemPartnerItem
        "
      >
        <button-fi
          v-if="localRetailers"
          type="button"
          class="product-family-head-variant-fi__action__local-retailer button-fi__primary button-fi--large"
          :disabled="!hasLocalRetailers"
          @click.stop.prevent="openRetailSearchDialog"
        >
          {{ $dict.get('dealer-Locator.Find-your-local-Dealer') }}
        </button-fi>

        <button-fi
          v-if="isRetailConnect"
          type="button"
          :class="[
            'product-family-head-variant-fi__action__product-retailer',
            localRetailers ? 'button-fi__tertiary--large' : 'button-fi__primary--large',
          ]"
          :disabled="retailers === null || retailers.length === 0"
          @click.stop.prevent="openProductRetailerDialog"
        >
          {{ $dict.get('online-Shop.Product.Retail-Connect.Buy-Product') }}
        </button-fi>

        <button-fi
          v-if="isRetailConnectCart"
          type="button"
          :class="[
            'product-family-head-variant-fi__action__add-to-retail-connect-cart',
            localRetailers ? 'button-fi__tertiary--large' : 'button-fi__primary--large',
          ]"
          :disabled="Boolean(retailerNotAvailable || productInCart || addToCartLoading)"
          @click.stop.prevent="addProduct"
        >
          {{
            productInCart
              ? $dict.get('online-Shop.Product.Already-in-Cart')
              : $dict.get('online-Shop.Product.Add-to-Cart')
          }}
        </button-fi>
      </template>

      <button-fi
        v-else
        :href="variantSlug"
        class="product-family-head-variant-fi__action__go-to-variant button-fi__primary button-fi--large"
        data-test="to-variant"
      >
        {{ $dict.get('product-Family.Facets.Variant.To-Variant-Page') }}
      </button-fi>
    </div>
  </div>

  <add-to-memo-list-dialog-fi
    v-if="!isAppView && variant"
    ref="addToMemoListDialog"
    :product="variant.ID"
    :quantity="quantity"
    multiple
    @error-occured="enterErrorState"
  />

  <retail-search-dialog-fi
    ref="retailSearchDialog"
    :catalog-id="variant.ID"
    :product-name="catalogData.ProductName"
  />

  <product-retailer-dialog-fi
    ref="productRetailerDialog"
    :retailers="retailers"
    :retailer-facets="retailerFacets"
  />
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import {
  AvailabilityStatus,
  BasePriceFi,
  ButtonFi,
  LinkFi,
  LoadingIndicator,
  LoadingIndicatorWrapper,
  PriceSavingsFi,
  ProductPackageSizeFi,
  QuantityModificationHintFi,
} from 'atoms';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

import AddToMemoListDialogFi from '@/components/memo-list/add-to-memo-list-dialog-fi/AddToMemoListDialogFi.vue';
import ProductRetailerDialogFi from '@/components/product-detail/product-retailer-dialog-fi/ProductRetailerDialogFi.vue';
import RetailSearchDialogFi from '@/components/product-detail/retail-search-dialog-fi/RetailSearchDialogFi.vue';
import QuantitySteps from '@/components/shop-modules/quantity-steps/QuantitySteps.vue';
import { useEventHub } from '@/composables/eventHub';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useOnlineRetailers } from '@/composables/onlineRetailers';
import { readPriceUnit } from '@/composables/preferredOrderUnit';
import { useSeefelder } from '@/composables/seefelder';
import { capToPackagingUnit, useSeefelderStockCap } from '@/composables/seefelderStockCap';
import { useShoppingcart } from '@/composables/shoppingcart';
import { useSlugLink } from '@/composables/slugLink';
import { useSystemPartner } from '@/composables/systemPartner';
import { useMemoListFunction } from '@/composables/useMemoList';
import { useUser } from '@/composables/user';
import GET_BEST_PRICE from '@/graphql/getBestPrice.gql';
import GET_RETAILER_AVAILABILITY_FOR_IDS from '@/graphql/getRetailerAvailabilityForIds.gql';
import HAS_LOCAL_RETAILERS from '@/graphql/hasLocalRetailers.gql';
import { useNavigationStore } from '@/store/pinia/navigation';
import { useNotificationStore } from '@/store/pinia/notification';

defineOptions({
  name: 'ProductFamilyHeadVariantFi',
});

const navigationStore = useNavigationStore();
const fischer = useFischer();
const dict = useDict();
const { logoutIfTokenInvalid } = useUser();
const { sendClickToEventHub } = useEventHub();
const isAppView = fischer.app.isAppView();
const isShop = computed(() => fischer.shop.isClosedShop() || fischer.shop.isOnlineShop());
const isRetailConnect = computed(() => fischer.shop.isRetailConnect());
const isRetailConnectCart = computed(() => fischer.shop.isRetailConnectCart());
const localRetailers = computed(() => fischer.localRetailers());
const { openGeneralErrorNotification } = useNotificationStore();
const { getSlugLink } = useSlugLink();

const props = defineProps<{
  variant: any;
}>();

const addToMemoListDialog = useTemplateRef('addToMemoListDialog');
const retailSearchDialog = useTemplateRef('retailSearchDialog');
const productRetailerDialog = useTemplateRef('productRetailerDialog');

const quantity = ref(0);
const bestPrice: any = ref(null);
const addToCartLoading = ref(false);

const isAvailable = computed(
  () => bestPrice.value?.deliverySpeed && bestPrice.value.deliverySpeed !== 'NODELIVERY',
);

const useMemoList = computed(() => useMemoListFunction(fischer));

const catalogData = computed(() => props.variant.catalogProductData);

const { isSystemItem } = useSystemPartner();
const isSystemPartnerItem = computed(() => isSystemItem(catalogData.value));

const variantSlug = computed(() => getSlugLink(catalogData.value) || '');

const { isSeefelder } = useSeefelder(() => catalogData.value);

const { remainingStock } = useSeefelderStockCap(() => props.variant.ID, isSeefelder);

const stockCap = computed<number | null>(() => {
  if (remainingStock.value === null) {
    return null;
  }
  return capToPackagingUnit(
    remainingStock.value,
    bestPrice.value?.quantityStepRanges,
    Boolean(bestPrice.value?.partPackagingAllowed),
  );
});

// Also not orderable when the remainder is below the minimum order quantity.
const isNotOrderable = computed(
  () =>
    stockCap.value === 0 ||
    (stockCap.value !== null && Number(bestPrice.value?.initialQuantity ?? 0) > stockCap.value),
);

// 0 makes QuantitySteps ignore the cap; this surface hides the stepper instead.
const cappedQuantity = computed(() => (isNotOrderable.value ? 0 : stockCap.value));

const quantityModificationHint = ref('');
const quantityModificationDataTest2 = ref<string | null>(null);

const onValueRounded = (hint: string, dataTest2?: string) => {
  quantityModificationHint.value = hint;
  quantityModificationDataTest2.value = dataTest2 ?? null;
};

const hasLocalRetailers = computed(() => hasLocalRetailersResult.value?.hasLocalRetailers ?? false);

const getRetailerAvailabilityForIds = computed(
  () => retailerAvailabilityResult.value?.getRetailerAvailabilityForIds || [],
);

const productIds = computed(() => [props.variant.ID]);

const retailerNotAvailable = computed(() => {
  const stock = getRetailerAvailabilityForIds.value?.[0]?.stock;
  return stock === 'empty' || stock === null;
});

const unitNotSupported = ref<boolean>(false);

const orderUnit = computed<string | undefined>(() =>
  unitNotSupported.value ? undefined : readPriceUnit(fischer),
);

const bestPriceVariables = computed(() => {
  return {
    baseSiteId: fischer.baseSiteId(),
    accessToken: fischer.user.accessToken(),
    productCode: props.variant.ID,
    qty: Number.parseInt(String(quantity.value), 10),
    unit: orderUnit.value,
    language: fischer.siteLanguage(),
    userId: fischer.user.id(),
    catalogLocale: fischer.catalogLocale(),
  };
});

onMounted(() => {
  if (!IS_SSR && (isRetailConnectCart.value || isShop.value)) {
    getCurrentCart();
  }
});

const getCurrentCart = () => {
  if (fischer.cartId()) {
    try {
      getCart(false, true);
    } catch (error) {
      console.error('could not get current cart!', error);
    }
  }
};

const enterErrorState = (...errors: any[]) => {
  console.error(...errors);
  openGeneralErrorNotification();
};

const bestPriceVarsRef = ref({ ...bestPriceVariables.value });
const {
  fetching: loadingBestPrice,
  data: bestPriceQueryData,
  error: bestPriceQueryError,
  executeQuery: executeBestPriceQuery,
} = useQuery({
  query: GET_BEST_PRICE,
  variables: bestPriceVarsRef,
  pause: true,
  requestPolicy: 'network-only',
});

watch(bestPriceQueryError, async (error) => {
  if (error) {
    await logoutIfTokenInvalid(error);
    enterErrorState('error getting best price', error);
  }
});

watch(bestPriceQueryData, (queryData) => {
  if (!queryData?.getBestPrice?.price && orderUnit.value) {
    unitNotSupported.value = true;
    bestPriceVarsRef.value = { ...bestPriceVariables.value };
    executeBestPriceQuery();
    return;
  }
  if (queryData?.getBestPrice) {
    quantity.value = queryData.getBestPrice.requestedQuantity;
    bestPrice.value = queryData.getBestPrice;
  }
});

const { data: hasLocalRetailersResult } = useQuery({
  query: HAS_LOCAL_RETAILERS,
  variables: computed(() => ({
    brand: fischer.brandId(),
    productId: props.variant.ID || '',
    locale: fischer.catalogLocale(),
  })),
  pause: computed(() => IS_SSR || !localRetailers.value || !props.variant.ID),
  requestPolicy: 'network-only',
});

const { addProductToCart, getCart, cart } = useShoppingcart({
  enterErrorState,
  logoutIfTokenInvalid,
});

const productInCart = computed(() => {
  let inCart = false;
  if (cart.value?.products) {
    inCart = cart.value.products.some((product) => product?.articleNo === props.variant.ID);
  }
  return inCart;
});

const { data: retailerAvailabilityResult } = useQuery({
  query: GET_RETAILER_AVAILABILITY_FOR_IDS,
  variables: computed(() => ({
    baseSiteId: fischer.baseSiteId(),
    productIds: productIds.value,
    useGtins: fischer.useGtins(),
  })),
  pause: computed(() => IS_SSR || !isRetailConnectCart.value || productInCart.value),
});

const updateQuantity = async (qty: string) => {
  if (qty === null || qty === undefined) {
    return;
  }

  quantityModificationHint.value = '';
  quantityModificationDataTest2.value = null;

  quantity.value = Number.parseInt(String(qty), 10);
  bestPriceVarsRef.value = { ...bestPriceVariables.value };
  await executeBestPriceQuery();
};

const { retailers, retailerFacets } = useOnlineRetailers({
  productIds,
  fischer,
  dict,
  isRetailConnect,
  isRetailConnectCart,
});

watch(
  () => props.variant,
  async () => {
    if (!isShop.value) {
      return;
    }

    quantity.value = 0;
    bestPrice.value = null;
    unitNotSupported.value = false;
    quantityModificationHint.value = '';
    quantityModificationDataTest2.value = null;
    bestPriceVarsRef.value = { ...bestPriceVariables.value };
    await executeBestPriceQuery();
  },
  {
    immediate: true,
  },
);

const addProduct = () => {
  addToCartLoading.value = true;
  addProductToCart({
    productID: props.variant.ID,
    productRequestedQuantity: quantity.value,
    productInitialQuantity: bestPrice.value?.initialQuantity ?? undefined,
    unit: bestPrice.value?.price?.unit ?? undefined,
  })
    .then((res) => {
      if (res?.success) {
        addToCartLoading.value = false;
      } else {
        console.error('could not add product to cart', res);
        openGeneralErrorNotification();
        addToCartLoading.value = false;
      }
    })
    .catch((err) => {
      console.error(err);
      openGeneralErrorNotification();
      addToCartLoading.value = false;
    });
};

const addToMemoList = () => {
  addToMemoListDialog.value?.show();
};

const openRetailSearchDialog = () => {
  // TRACKING EVENT
  if (!IS_SSR) {
    const productNameValue = `${props.variant.ID} - ${catalogData.value?.ProductName}`;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'RCBuyLocal',
      productName: productNameValue,
    });
  }

  retailSearchDialog.value?.show();
};

const openProductRetailerDialog = () => {
  // TRACKING EVENT
  if (!IS_SSR) {
    const productNameValue = `${props.variant.ID} - ${catalogData.value?.ProductName}`;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'RCBuyProduct',
      productName: productNameValue,
    });
  }

  productRetailerDialog.value?.show();
};

const onProductLinkClick = (event: MouseEvent, href: string | null) => {
  const productString = navigationStore.productBreadcrumb.map((item: any) => item.title).join('/');
  const clickSource = fischer.isProductSeriesEnabled() ? 'series' : 'family';
  if (!href) {
    console.error('missing href in product click event');
    return;
  }

  sendClickToEventHub(
    {
      url: window.location.href,
      clickSource: clickSource,
      pageTitle: catalogData.value?.ProductName,
      productId: catalogData.value?.ProductId,
      productTitle: catalogData.value?.ProductName,
      query: `[${clickSource}]:/${productString}/${catalogData.value?.ProductName}`,
    },
    event,
    href,
  );
};
</script>

<style scoped src="./product-family-head-variant-fi" />
